// Typing on the phone page: a typed turn (`user.text`) goes to the brain exactly like a spoken
// one. It goes into the running session, whoever runs it: the phone's own voice session (the orb
// is on: the reply is spoken on the phone), or the PC's (spoken on the PC). Typing never
// interrupts a reply being spoken or paused. With no session the orb is off, and with the orb
// off nothing is voiced: it starts a text-only session (`audio:"none"`: the reply streams into
// the transcript, nothing is spoken, no "Speaking"). Voice comes on only with a tap on the orb.
// (`speak:true` keeps the 0.7 behaviour: `audio:"client"` with `mic:false`, spoken here.)
// Typing never opens the mic.
// Attached files (./attach.ts) go with it, named by their upload ids; the text may then be empty.
import type { VoiceKitClient } from "../client";

export type Typist = Pick<VoiceKitClient, "sessionId" | "startSession" | "stopSession" | "say"> & {
  /** No player: nothing to speak with, so the reply is only shown. */
  readonly player?: unknown;
};

export interface TypedOptions {
  /** With no session running: speak the reply on this page. Default `false`: the orb is off,
   * so the reply is text only. */
  speak?: boolean;
}

/** Sends `text` (and `attachments`) as a typed turn. False (and nothing sent) when both are empty. */
export async function sendTyped(c: Typist, text: string, attachments: string[] = [], opts: TypedOptions = {}): Promise<boolean> {
  const t = text.trim();
  if (!t && !attachments.length) return false;
  const started = c.sessionId === null;
  const speak = (opts.speak ?? false) && !!c.player;
  if (started) await c.startSession(speak ? { audio: "client", mic: false } : { audio: "none" });
  try {
    await (attachments.length ? c.say(t, { attachments }) : c.say(t));
  } catch (e) {
    // A kit that refuses typing from a phone (0.3.0 and earlier): don't leave behind the
    // text-only session this started for it.
    if (started) void c.stopSession("typing refused").catch(() => {});
    throw e;
  }
  return true;
}

/** What to show when a typed turn fails. */
export function typedError(code: string, message: string): string {
  if (code === "forbidden") return "This PC's voice kit doesn't take typed messages yet. Update the app on your PC.";
  if (code === "session_active") return "The PC is busy starting a conversation. Try again in a moment.";
  if (code === "no_attachment") return "An attachment didn't reach the PC. Remove it and attach it again.";
  return message;
}
